import { useState } from 'react'
import { Link } from 'react-router-dom'
import PrivateValue from './PrivateValue'
import TradeDetailContent from './TradeDetailContent'
import { useSettings } from '../hooks/useSettings'
import { getUserTimezone } from '../lib/timezone'

const money = value => value == null ? '—' : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

export default function SetupTradeDetailContent({ group, nicknameMap = {} }) {
  const [selectedTrade, setSelectedTrade] = useState(null)
  const { settings } = useSettings()
  const timezone = getUserTimezone(settings)
  const accountName = value => nicknameMap[value] || `···${String(value || '').slice(-4)}`
  return (
    <div className="overflow-y-auto p-5 space-y-5">
      <header className="pr-16">
        <div className="font-mono text-xs text-inkMuted">Setup review · {group.status}</div>
        <h2 className="text-xl font-bold">{group.symbol} · {group.setupLabel}</h2>
        <p className="text-sm text-inkMuted">{group.accounts.length} accounts · {group.trades.length} account trade records</p>
      </header>
      <div className="bg-canvas2 rounded-lg p-3 text-xs space-y-1">
        <p>Grouped by recorded account and entry orders. Setup attribution and account-statement reconciliation are separate checks.</p>
        <p>Amounts cover the attributed account records shown below. Any linked records that need reconciliation are excluded from setup statistics.</p>
        {group.dataIssues.map(issue => <p key={issue} className="text-ember">{issue}</p>)}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><div className="text-xs text-inkMuted">Recorded net P&amp;L</div><PrivateValue>{money(group.net_pnl)}</PrivateValue></div>
        <div><div className="text-xs text-inkMuted">Entered quantity</div><PrivateValue>{group.quantity_total}</PrivateValue></div>
        <div><div className="text-xs text-inkMuted">Remaining quantity</div><PrivateValue>{group.quantity_open}</PrivateValue></div>
      </div>
      <section>
        <h3 className="font-semibold mb-2">Account breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead><tr>{['Account', 'Status', 'Entered / remaining', 'Avg entry', 'Avg exit', 'Fees', 'Net P&L'].map(label => <th key={label} className="p-2">{label}</th>)}</tr></thead>
            <tbody>{group.accounts.map(account => <tr key={account.account_number} className="border-t border-border">
              <td className="p-2">{accountName(account.account_number)}</td><td className="p-2">{account.status}</td>
              <td className="p-2"><PrivateValue>{account.quantity_total} / {account.quantity_open}</PrivateValue></td>
              <td className="p-2"><PrivateValue>{money(account.avg_entry_price)}</PrivateValue></td>
              <td className="p-2"><PrivateValue>{money(account.avg_exit_price)}</PrivateValue></td>
              <td className="p-2"><PrivateValue>{money(account.commission == null || account.fees == null ? null : account.commission + account.fees)}</PrivateValue></td>
              <td className="p-2"><PrivateValue>{money(account.net_pnl)}</PrivateValue></td>
            </tr>)}</tbody>
          </table>
        </div>
      </section>
      <section className="space-y-2">
        <h3 className="font-semibold">Original setup context</h3>
        <p className="text-sm whitespace-pre-wrap">{group.project.thesis || 'No thesis recorded.'}</p>
        <p className="text-sm whitespace-pre-wrap">{group.project.setup_notes || 'No setup notes recorded.'}</p>
        <p className="text-xs text-inkMuted">Original setup risk has not been verified; setup R-multiple is unavailable.</p>
      </section>
      <section>
        <h3 className="font-semibold mb-2">Executions ({group.executions.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead><tr>{['Time', 'Account', 'Execution', 'Quantity', 'Price', 'Order / source'].map(label => <th className="p-2" key={label}>{label}</th>)}</tr></thead>
            <tbody>{group.executions.map(e => <tr key={e.id} className="border-t border-border">
              <td className="p-2 whitespace-nowrap">{e.executed_at ? new Date(e.executed_at).toLocaleString('en-US', { timeZone: timezone }) : 'Unknown'}</td>
              <td className="p-2">{accountName(e.account_number)}</td><td className="p-2">{e.execution_type.replace('_', ' ')}</td>
              <td className="p-2"><PrivateValue>{e.quantity}</PrivateValue></td><td className="p-2"><PrivateValue>{money(e.price)}</PrivateValue></td>
              <td className="p-2"><PrivateValue>{e.order_id || 'Missing order'}</PrivateValue><div className="text-inkMuted">{e.import_source || 'Unknown source'}</div></td>
            </tr>)}</tbody>
          </table>
        </div>
      </section>
      <section className="space-y-2">
        <h3 className="font-semibold">Existing account reviews</h3>
        <p className="text-xs text-inkMuted">Existing notes and reviews remain attached to their account records.</p>
        {group.trades.map(trade => <button key={trade.id} type="button" className="block text-sm underline"
          onClick={() => setSelectedTrade(selectedTrade === trade.id ? null : trade.id)}>
          {accountName(trade.account_number)} · {trade.entry_date} · view account review
        </button>)}
        {selectedTrade && <TradeDetailContent key={selectedTrade} tradeId={selectedTrade} nicknameMap={nicknameMap} />}
      </section>
      <Link className="text-xs underline" to="/positions/history">Back to Trade Log</Link>
    </div>
  )
}
